import { z } from 'zod';

/**
 * When a public build variable (`VITE_*` / `NEXT_PUBLIC_*`) must be set
 * (ADR-0029 §1):
 * - `build`: every production build (`vite build`, `next build`), deployed or
 *   not. The dev server may leave it unset.
 * - `deployed`: builds for a deployed stage (`dev` / `prod`) only. Local builds
 *   may leave it unset.
 * - `optional`: never required.
 */
export type ClientEnvRequirement = 'build' | 'deployed' | 'optional';

export type ClientEnvVar = {
  /** Validates a set value (already trimmed, never empty). */
  schema: z.ZodType<string>;
  required: ClientEnvRequirement;
};

export type ClientEnvSpec = Record<string, ClientEnvVar>;

/** Typed runtime view: `build` keys are always set outside the dev server. */
export type ClientEnv<S extends ClientEnvSpec> = {
  readonly [K in keyof S]: S[K]['required'] extends 'build'
    ? string
    : string | undefined;
};

export type ClientEnvSource = Readonly<Record<string, unknown>>;

export type ClientEnvBuildTarget = {
  command: 'serve' | 'build';
  /** Build for a deployed stage (`dev` / `prod`). */
  deployed: boolean;
};

export type ClientEnvIssue = {
  key: string;
  problem: 'missing' | 'invalid' | 'unknown' | 'secret' | 'conflict';
  message?: string;
};

/** Stages with a committed `config/<stage>.json` per frontend (ADR-0029 §1). */
export const BUILD_STAGES = ['local', 'dev', 'prod'] as const;

export type BuildStage = (typeof BUILD_STAGES)[number];

/** Declares a workspace's public build variables: the single list of keys. */
export function defineClientEnv<const S extends ClientEnvSpec>(spec: S): S {
  return spec;
}

/** Absolute `http(s)` URL (API origins, domains). Callers strip a trailing slash. */
export function httpUrl(): z.ZodType<string> {
  return z.url({ protocol: /^https?$/, error: 'must be an http(s) URL' });
}

/** Absolute `https` URL (console links, hosted media). */
export function httpsUrl(): z.ZodType<string> {
  return z.url({ protocol: /^https$/, error: 'must be an https URL' });
}

export function nonEmpty(): z.ZodType<string> {
  return z.string().min(1);
}

function readValue(source: ClientEnvSource, key: string): string | undefined {
  const raw = source[key];
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const VERCEL_TARGET_STAGE: Readonly<Record<string, BuildStage>> = {
  production: 'prod',
  preview: 'dev',
};

/**
 * The stage whose `config/<stage>.json` a build reads (ADR-0029 §2), first
 * match wins: `UP2PROMO_STAGE`; on Vercel, the target environment
 * (`production` → `prod`, `preview` → `dev`); the dev server → `local`.
 * Any other production build fails: it must name its stage.
 */
export function resolveBuildStage(
  source: ClientEnvSource,
  command: 'serve' | 'build',
): BuildStage {
  const explicit = readValue(source, 'UP2PROMO_STAGE');
  if (explicit !== undefined) {
    if ((BUILD_STAGES as readonly string[]).includes(explicit)) {
      return explicit as BuildStage;
    }
    throw new Error(
      `UP2PROMO_STAGE=${explicit} is not a stage (${BUILD_STAGES.join(' | ')}).`,
    );
  }
  if (readValue(source, 'VERCEL') === '1') {
    const target =
      readValue(source, 'VERCEL_TARGET_ENV') ?? readValue(source, 'VERCEL_ENV');
    const stage =
      target === undefined ? undefined : VERCEL_TARGET_STAGE[target];
    if (stage) return stage;
    throw new Error(
      `Vercel target environment "${target ?? ''}" has no stage; set UP2PROMO_STAGE.`,
    );
  }
  if (command === 'serve') return 'local';
  throw new Error(
    'No build stage: set UP2PROMO_STAGE=local | dev | prod (ADR-0029 §2).',
  );
}

function isRequired(
  requirement: ClientEnvRequirement,
  target: ClientEnvBuildTarget,
): boolean {
  if (target.command !== 'build') return false;
  if (requirement === 'build') return true;
  return requirement === 'deployed' && target.deployed;
}

/** Checks `source` against the spec. */
export function validateClientEnv(
  spec: ClientEnvSpec,
  source: ClientEnvSource,
  target: ClientEnvBuildTarget,
): { values: Record<string, string>; issues: ClientEnvIssue[] } {
  const values: Record<string, string> = {};
  const issues: ClientEnvIssue[] = [];

  for (const [key, entry] of Object.entries(spec)) {
    const value = readValue(source, key);
    if (value === undefined) {
      if (isRequired(entry.required, target)) {
        issues.push({ key, problem: 'missing' });
      }
      continue;
    }
    const parsed = entry.schema.safeParse(value);
    if (!parsed.success) {
      issues.push({
        key,
        problem: 'invalid',
        message: parsed.error.issues.map((issue) => issue.message).join('; '),
      });
      continue;
    }
    values[key] = parsed.data;
  }

  return { values, issues };
}

export function formatClientEnvIssues(
  app: string,
  stage: BuildStage,
  issues: ClientEnvIssue[],
): string {
  const lines = issues.map(({ key, problem, message }) => {
    switch (problem) {
      case 'missing':
        return `  - ${key} is required but not set`;
      case 'unknown':
        return `  - ${key} is not in the env schema (src/env.schema.ts)`;
      case 'secret':
        return `  - ${key} looks like a secret: ${message ?? ''}`;
      case 'conflict':
        return `  - ${key} is set in the build env and differs from config/${stage}.json`;
      default:
        return `  - ${key} is invalid: ${message ?? 'rejected by the schema'}`;
    }
  });
  const hint =
    stage === 'local'
      ? `Set the values in ${app}/config/local.json, or override them in ${app}/.env (see .env.example).`
      : `config/${stage}.json is the only source for ${stage} builds: fix the file, and remove ${app}'s VITE_* / NEXT_PUBLIC_* values from the build env (Vercel / Capawesome dashboard).`;
  return [
    `[${app}] Invalid public build config for stage ${stage} (ADR-0029):`,
    ...lines,
    hint,
  ].join('\n');
}

/** Keys that name a secret; such a value can't be a public build variable. */
const SECRET_KEY = /SECRET|PASSWORD|PRIVATE/;

/** Value shapes of credentials that must never ship to a browser. */
const SECRET_VALUES: readonly [RegExp, string][] = [
  [/-----BEGIN [A-Z ]*PRIVATE KEY-----/, 'private key'],
  [/\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/, 'AWS access key id'],
  [/\b(?:sk|rk)_(?:live|test)_[0-9A-Za-z]+/, 'Stripe secret key'],
  [/\bwhsec_[0-9A-Za-z]+/, 'Stripe webhook secret'],
  [/\bphx_[0-9A-Za-z]+/, 'PostHog personal API key'],
  [/\b(?:ghp|gho|ghs|ghu)_[0-9A-Za-z]+|\bgithub_pat_/, 'GitHub token'],
  [/\bxox[abpr]-/, 'Slack token'],
  [/^sk-[0-9A-Za-z_-]{20,}/, 'OpenAI / API secret key'],
  [/\bsk\.eyJ/, 'Mapbox secret token'],
  [/\beyJ[\w-]+\.eyJ[\w-]+\.[\w-]+/, 'JWT'],
  [/:\/\/[^/\s:@]+:[^/\s@]+@/, 'URL with credentials'],
];

/** Why `key=value` looks like a secret, or `undefined` when it doesn't. */
export function secretLikeReason(
  key: string,
  value: string,
): string | undefined {
  if (SECRET_KEY.test(key)) return 'the key names a secret';
  return SECRET_VALUES.find(([pattern]) => pattern.test(value))?.[1];
}

/**
 * Checks a parsed `config/<stage>.json`: a flat object of strings whose keys
 * are all declared in the spec and whose values don't look like secrets.
 */
export function checkStageConfig(
  spec: ClientEnvSpec,
  config: unknown,
): { config: Record<string, string>; issues: ClientEnvIssue[] } {
  const issues: ClientEnvIssue[] = [];
  const values: Record<string, string> = {};
  if (typeof config !== 'object' || config === null || Array.isArray(config)) {
    return {
      config: values,
      issues: [
        { key: '(file)', problem: 'invalid', message: 'not a JSON object' },
      ],
    };
  }
  for (const [key, value] of Object.entries(config)) {
    if (!(key in spec)) {
      issues.push({ key, problem: 'unknown' });
      continue;
    }
    if (typeof value !== 'string') {
      issues.push({ key, problem: 'invalid', message: 'must be a string' });
      continue;
    }
    const reason = secretLikeReason(key, value);
    if (reason) {
      issues.push({ key, problem: 'secret', message: reason });
      continue;
    }
    values[key] = value;
  }
  return { config: values, issues };
}

/**
 * The env a build validates: the stage file, plus the build env.
 * `local`: the build env (`.env`, shell) overrides the file.
 * `dev` / `prod`: the file is the only source; a schema key set in the build
 * env to anything else (e.g. a leftover dashboard var) is a conflict.
 */
export function applyStageConfig(
  spec: ClientEnvSpec,
  stage: BuildStage,
  config: Readonly<Record<string, string>>,
  source: ClientEnvSource,
): { source: Record<string, unknown>; issues: ClientEnvIssue[] } {
  const merged: Record<string, unknown> = { ...source };
  const issues: ClientEnvIssue[] = [];
  for (const key of Object.keys(spec)) {
    const fromEnv = readValue(source, key);
    const fromFile = readValue(config, key);
    if (stage !== 'local' && fromEnv !== undefined && fromEnv !== fromFile) {
      issues.push({ key, problem: 'conflict' });
    }
    const value = stage === 'local' ? (fromEnv ?? fromFile) : fromFile;
    if (value === undefined) delete merged[key];
    else merged[key] = value;
  }
  return { source: merged, issues };
}

/**
 * Runtime accessor. Reads `source` on every access, so tests can stub env.
 * Reading an unset `build` key throws: outside the dev server the build has
 * already rejected it, so it only happens in tests or a mis-built bundle.
 */
export function readClientEnv<const S extends ClientEnvSpec>(
  spec: S,
  source: ClientEnvSource,
): ClientEnv<S> {
  const env: Record<string, string | undefined> = {};
  for (const [key, entry] of Object.entries(spec)) {
    Object.defineProperty(env, key, {
      enumerable: true,
      get: () => {
        const value = readValue(source, key);
        if (value === undefined && entry.required === 'build') {
          throw new Error(`${key} is not set (see .env.example).`);
        }
        return value;
      },
    });
  }
  return Object.freeze(env) as ClientEnv<S>;
}

const EXAMPLE_KEY_LINE = /^\s*#?\s*([A-Z][A-Z0-9_]*)\s*=/;

/** Keys in a `.env.example`, including commented-out (`# KEY=`) entries. */
export function parseEnvExampleKeys(text: string): string[] {
  const keys = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const key = EXAMPLE_KEY_LINE.exec(line)?.[1];
    if (key) keys.add(key);
  }
  return [...keys];
}

/** Spec keys missing from the example file, and example keys not in the spec. */
export function diffEnvExample(
  spec: ClientEnvSpec,
  exampleText: string,
): { missing: string[]; unknown: string[] } {
  const exampleKeys = new Set(parseEnvExampleKeys(exampleText));
  const specKeys = Object.keys(spec);
  return {
    missing: specKeys.filter((key) => !exampleKeys.has(key)),
    unknown: [...exampleKeys].filter((key) => !(key in spec)),
  };
}
